/**
 * ★ التقويم الميلادي حصراً — وتاريخ رقمي مبني يدوياً بلا Intl
 *
 * المشكلة (حدثت مرتين): كل صيغ Intl.DateTimeFormat للعربية — بما فيها
 * ar-SY — تُدخِل علامة اتجاه خفية (RLM, U+200F) بين أجزاء التاريخ:
 *   "24" + RLM + "/09" + RLM + "/2026"
 * حتى بعد حذف هذه العلامة، يبقى النص عرضة لإعادة ترتيب Unicode Bidi
 * إن ظهر داخل عنصر لا يحمل dir="ltr" بالضبط — وهذا هشّ لأنه يعتمد على
 * أن كل مكان يُستخدم فيه يتذكّر إضافة الخاصية.
 *
 * الحل: لا نستخدم Intl إطلاقاً للتواريخ الرقمية (DD/MM/YYYY وHH:mm).
 * نبنيها بأنفسنا كنص عادي، ونُطوّقها بعلامتي عزل اتجاه صريحتين
 * (LRI...PDI) مضمّنتين في النص نفسه. هذا يجعل الاتجاه الصحيح خاصية
 * ذاتية في النص، تعمل في أي مكان يُعرض فيه — جدول، تصدير، طباعة —
 * دون الحاجة لأي خاصية HTML خارجية.
 *
 * التنسيق الطويل بالعربية (الاسم الطويل للشهر) يبقى عبر Intl، لأن
 * اختلاط كلمات عربية حقيقية بالأرقام لا يُظهر هذه المشكلة عملياً.
 *
 * كل التواريخ — تخزيناً ونقلاً — بصيغة ISO الميلادية YYYY-MM-DD.
 */

/// عزل اتجاه صريح: يضمن عرض المحتوى من اليسار لليمين في أي سياق نصي
const LRI = "\u2066"; // Left-to-Right Isolate
const PDI = "\u2069"; // Pop Directional Isolate
const ltr = (s: string) => `${LRI}${s}${PDI}`;

const LOCALE = "ar-SY-u-ca-gregory-nu-latn";
const LONG_FMT = new Intl.DateTimeFormat(LOCALE, {
  calendar: "gregory", numberingSystem: "latn",
  weekday: "long", year: "numeric", month: "long", day: "numeric",
});
/** يزيل أي علامة اتجاه خفية قد يُدرجها Intl */
const stripBidi = (s: string) => s.replace(/[\u200e\u200f\u061c]/g, "");

const pad = (n: number) => String(n).padStart(2, "0");

/** يحوّل "2026-03-15" إلى كائن تاريخ دون انزلاق المنطقة الزمنية */
const parse = (iso: string) => new Date(`${iso}T00:00:00Z`);

/** 2026-03-15 → "15/03/2026" — نص مبني يدوياً، لا Intl */
export function formatDate(iso: string): string {
  if (!iso) return "";
  const d = parse(iso);
  return ltr(`${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()}`);
}

/** 2026-03-15 → "الأحد، 15 آذار 2026" ميلادي — نص عربي حقيقي، Intl آمن هنا */
export const formatDateLong = (iso: string) => (iso ? stripBidi(LONG_FMT.format(parse(iso))) : "");

/** طابع زمني كامل من ISO datetime → "15/03/2026 14:05" — مبني يدوياً */
export function formatDateTime(isoTs: string): string {
  if (!isoTs) return "";
  const d = new Date(isoTs);
  const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return ltr(`${date} ${time}`);
}

/** أرقام بفواصل آلاف وأرقام لاتينية — مطوَّقة بعزل اتجاه للأمان */
export const formatNumber = (n: number, maxFractionDigits = 3) =>
  ltr(new Intl.NumberFormat("en-US", { maximumFractionDigits: maxFractionDigits }).format(n));

// ── أدوات المدى التاريخي (ميلادية، UTC، بلا انزلاق) ───────────────────────

export const toISO = (d: Date) => d.toISOString().slice(0, 10);

export const todayISO = () => toISO(new Date());

export function addDays(iso: string, days: number): string {
  const d = parse(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toISO(d);
}

export function monthStartISO(offset = 0): string {
  const n = new Date();
  return toISO(new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth() + offset, 1)));
}

export function monthEndISO(offset = 0): string {
  const n = new Date();
  return toISO(new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth() + offset + 1, 0)));
}

export const daysInclusive = (from: string, to: string) =>
  Math.round((parse(to).getTime() - parse(from).getTime()) / 86_400_000) + 1;

/** أسماء الأشهر الميلادية كما تُستخدم في سوريا وبلاد الشام */
export const GREGORIAN_MONTHS = [
  "كانون الثاني", "شباط", "آذار", "نيسان", "أيار", "حزيران",
  "تموز", "آب", "أيلول", "تشرين الأول", "تشرين الثاني", "كانون الأول",
] as const;
